import { useEffect, useMemo, useRef, useState } from "react";
import "../css/Loan.css";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

    /**
     * PersonalLoanLanding — Advanced multi-step loan wizard
     * - Fixed interest rate: 30% p.a.
     * - Live EMI preview, summary panel, save draft, validation hints
     */

    const INTEREST_RATE = 30; // percent p.a. fixed

    const formatINR = (n) =>
    typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : n;

    const calcEmi = (principal = 0, tenureMonths = 0, annualRate = INTEREST_RATE) => {
    const P = Number(principal) || 0;
    const N = Number(tenureMonths) || 0;
    if (!P || !N) return 0;
    const monthlyRate = annualRate / 12 / 100;
    const emi = (P * monthlyRate * Math.pow(1 + monthlyRate, N)) / (Math.pow(1 + monthlyRate, N) - 1);
    return Number.isFinite(emi) ? emi : 0;
    };

    const initialState = {
    // Step 1 – Personal
    fullName: "",
    email: "",
    mobile: "",
    dob: "",
    gender: "",
    pan: "",
    maritalStatus: "",
    education: "",
    state: "",
    city: "",
    address: "",

    // Step 2 – Employment
    employmentType: "",
    companyName: "",
    monthlyIncome: "",
    workExperience: "",
    officeAddress: "",
    salarySlip: null,

    // Step 3 – Banking & Loan
    bankName: "",
    accountNumber: "",
    ifsc: "",
    loanType: "Personal Loan",
    loanAmount: "",
    loanTenure: "",
    loanPurpose: "",

    // Step 4 – References & Docs
    ref1Name: "",
    ref1Mobile: "",
    ref2Name: "",
    ref2Mobile: "",
    idProof: null,
    };

 const PersonalLoanLanding = () => {
    const location = useLocation();
    const companyData = location.state?.company;
    console.log("superadmin id is ",companyData);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(() => {
        // restore saved draft if exists
        try {
        const raw = localStorage.getItem("tv_loan_draft");
        return raw ? { ...initialState, ...JSON.parse(raw) } : initialState;
        } catch {
        return initialState;
        }
    });

    const formRef = useRef(null);

    // Derived values
    const emi = useMemo(() => calcEmi(formData.loanAmount, formData.loanTenure, INTEREST_RATE), [
        formData.loanAmount,
        formData.loanTenure,
    ]);
    const monthlyEmiRounded = Math.round(emi);

    // Live validations (simple)
    const validations = {
        mobileValid: /^\d{10}$/.test(formData.mobile || ""),
        panValid: /^[A-Z]{5}\d{4}[A-Z]$/.test((formData.pan || "").toUpperCase()),
        incomeValid: Number(formData.monthlyIncome || 0) >= 1000,
        loanAmountValid: Number(formData.loanAmount || 0) > 0,
        tenureValid: Number(formData.loanTenure || 0) > 0,
    };

    useEffect(() => {
        // keep PAN uppercase
        if (formData.pan && formData.pan !== formData.pan.toUpperCase()) {
        setFormData((prev) => ({ ...prev, pan: prev.pan.toUpperCase() }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.pan]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
        }));
    };

    const jumpToStep = (num) => {
        setStep(num);
        // scroll to top of form for clarity
        if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth" });
    };

    const handleNext = () => {
        // Basic gated validation on next
        if (step === 1) {
        if (!formData.fullName || !validations.mobileValid || !validations.panValid) {
            alert("Please fill valid Full Name, Mobile (10 digits) and PAN to proceed.");
            return;
        }
        }
        if (step === 3) {
        // ensure loan fields valid
        if (!validations.loanAmountValid || !validations.tenureValid) {
            alert("Please enter Loan Amount and Loan Tenure to proceed.");
            return;
        }
        }
        if (step < 5) setStep((s) => s + 1);
        if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth" });
    };

    const handleBack = () => {
        if (step > 1) setStep((s) => s - 1);
        if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth" });
    };

    const handleSaveDraft = () => {
        try {
        const copy = { ...formData };
        // remove files from stored draft (cannot stringify File objects)
        if (copy.salarySlip) copy.salarySlip = { name: copy.salarySlip.name };
        if (copy.idProof) copy.idProof = { name: copy.idProof.name };
        localStorage.setItem("tv_loan_draft", JSON.stringify(copy));
        alert("Draft saved locally.");
        } catch (err) {
        console.error(err);
        alert("Failed to save draft.");
        }
    };

    const handleReset = () => {
        if (window.confirm("Reset the form? All unsaved changes will be lost.")) {
        setFormData(initialState);
        setStep(1);
        localStorage.removeItem("tv_loan_draft");
        }
    };

    // const handleSubmit = (e) => {
    //     e.preventDefault();

    //     // Final validation
    //     if (!validations.mobileValid || !validations.panValid) {
    //     alert("Please check mobile and PAN before submitting.");
    //     return;
    //     }
    //     // build payload (files would be uploaded to server in real implementation)
    //     const payload = { ...formData, emiPerMonth: monthlyEmiRounded, interestRate: INTEREST_RATE };
    //     console.log("Submitting payload:", payload);
        
    //     // TODO: integrate with backend API here
    //     alert("✅ Loan Application Submitted! (demo). Check console for payload.");
    //     localStorage.removeItem("tv_loan_draft");
    // };

    // helper to display file name or "Not added"
    
    const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation
    if (!validations.mobileValid || !validations.panValid) {
        alert("Please check mobile and PAN before submitting.");
        return;
    }

    if(!companyData){
        alert("SuperAdmin Id is required.");
        toast.error("SuperAdmin Id is required.");
        return;
    }

    console.log("Submitting formData:", formData);

    try {
        const response = await submitLoanApplication(formData);
        console.log("Server response:", response);
        alert("✅ Loan Application Submitted Successfully!");
        localStorage.removeItem("tv_loan_draft");
    } catch (error) {
        console.error("Submission error:", error);
        alert("❌ Failed to submit loan application. Please try again.");
    }
    };

    const fileName = (f) => (f ? (typeof f === "string" ? f : f.name) : "Not added");

    // console.log("formdata values are ",formData);

   
    const submitLoanApplication = async (formData) => {
    try {
        const data = new FormData();

        data.append('fullName', formData.fullName);
        data.append('email', formData.email);
        data.append('mobile', formData.mobile);
        data.append('dob', formData.dob);
        data.append('gender', formData.gender);
        data.append('pan', formData.pan);
        data.append('maritalStatus', formData.maritalStatus);
        data.append('education', formData.education);
        data.append('state', formData.state);
        data.append('city', formData.city);
        data.append('addressLine', formData.address);
        data.append('employmentType', formData.employmentType);
        data.append('companyName', formData.companyName);
        data.append('officeAddress', formData.officeAddress);
        data.append('bankName', formData.bankName);
        data.append('accountNumber', formData.accountNumber);
        data.append('ifscCode', formData.ifsc);
        data.append('loanType', formData.loanType);
        data.append('loanPurpose', formData.loanPurpose);
        data.append('reference1Name', formData.ref1Name);
        data.append('reference1Mobile', formData.ref1Mobile);
        data.append('reference2Name', formData.ref2Name);
        data.append('reference2Mobile', formData.ref2Mobile);
        data.append('status', "Pending");
        data.append('superAdminId', companyData.superAdminId);

        data.append('monthlyIncome', formData.monthlyIncome ? String(formData.monthlyIncome) : "");
        data.append('workExperienceYears', formData.workExperience ? String(formData.workExperience) : "");
        data.append('loanAmount', formData.loanAmount ? String(formData.loanAmount) : "");
        data.append('loanTenureMonths', formData.loanTenure ? String(formData.loanTenure) : "");

        // if (formData.idProof) {
        //     data.append('idProofPath', formData.idProof); 
        // }
        if (formData.salarySlip) {
            data.append('file', formData.salarySlip);
        }

        const response = await axios.post(
            "https://payyug.in/api/dashboard-loan/apply",
            data,
        );

        console.log("Loan application submitted:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error submitting loan application:", error.response?.data || error.message);
        throw error;
    }
};

  
return (
        <div className="loan-container" ref={formRef}>
        <div className="loan-header">
            <div>
            <h2 className="loan-title">Quick Loan Application Form</h2>
            <p className="loan-subtitle">
                Please provide your personal information accurately as it will be verified with your KYC
                documents.
            </p>
            </div>
            <div className="loan-actions-top">
            <button className="btn small secondary" onClick={handleSaveDraft} type="button">
                Save as Draft
            </button>
            <button className="btn small" onClick={handleReset} type="button">
                Reset
            </button>
            </div>
        </div>

        {/* main layout: form left, summary right */}
        <div className="loan-layout">
            <div className="loan-form-col">
            {/* Progress */}
            <div className="progress-bar">
                {[1, 2, 3, 4, 5].map((num) => {
                const cls = step > num ? "completed" : step === num ? "active" : "";
                return (
                    <div
                    key={num}
                    className={`step ${cls}`}
                    title={`Step ${num}`}
                    onClick={() => jumpToStep(num)}
                    >
                    {num}
                    </div>
                );
                })}
            </div>

            <form className="loan-form" onSubmit={handleSubmit}>
                {/* Step panels */}
                {step === 1 && (
                <section className="panel">
                    <h3 className="panel-title">Personal Information</h3>
                    <div className="form-grid">
                    <div className="form-field with-icon">
                        <label>Full Name (as per ID)</label>
                        <input
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        required
                        />
                        {/* <i className="fas fa-user" /> */}
                    </div>

                    <div className="form-field with-icon">
                        <label>Email Address</label>
                        <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@email.com"
                        />
                        {/* <i className="fas fa-envelope" /> */}
                    </div>

                    <div className="form-field with-icon">
                        <label>Mobile Number</label>
                        <input
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="Enter mobile number"
                        maxLength={10}
                        inputMode="numeric"
                        />
                        {/* <i className="fas fa-phone" /> */}
                        <small className={`hint ${validations.mobileValid ? "ok" : ""}`}>
                        {validations.mobileValid ? "Looks good" : "10 digits required"}
                        </small>
                    </div>

                    <div className="form-field with-icon">
                        <label>Date of Birth</label>
                        <input name="dob" type="date" value={formData.dob} onChange={handleChange} />
                        {/* <i className="fas fa-calendar" /> */}
                    </div>

                    <div className="form-field">
                        <label>Gender</label>
                        <select name="gender" value={formData.gender} onChange={handleChange}>
                        <option value="">Select gender</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                        </select>
                    </div>

                    <div className="form-field with-icon">
                        <label>PAN Number</label>
                        <input
                        name="pan"
                        value={formData.pan}
                        onChange={handleChange}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        />
                        {/* <i className="fas fa-id-card" /> */}
                        <small className={`hint ${validations.panValid ? "ok" : ""}`}>
                        {validations.panValid ? "PAN format valid" : "Enter valid PAN (eg. ABCDE1234F)"}
                        </small>
                    </div>

                    <div className="form-field">
                        <label>Marital Status</label>
                        <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                        <option value="">Select marital status</option>
                        <option>Single</option>
                        <option>Married</option>
                        <option>Other</option>
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Education Level</label>
                        <select name="education" value={formData.education} onChange={handleChange}>
                        <option value="">Select education</option>
                        <option>High School</option>
                        <option>Graduate</option>
                        <option>Post Graduate</option>
                        <option>Professional</option>
                        </select>
                    </div>

                    <div className="form-field">
                        <label>State</label>
                        <input name="state" value={formData.state} onChange={handleChange} />
                    </div>

                    <div className="form-field with-icon">
                        <label>City</label>
                        <input name="city" value={formData.city} onChange={handleChange} />
                        {/* <i className="fas fa-city" /> */}
                    </div>

                    <div className="form-field">
                        <label>Address Line</label>
                        <textarea name="address" value={formData.address} onChange={handleChange} />
                    </div>
                    </div>
                </section>
                )}

                {step === 2 && (
                <section className="panel">
                    <h3 className="panel-title">Employment & Income</h3>
                    <div className="form-grid">
                    <div className="form-field">
                        <label>Employment Type</label>
                        <select name="employmentType" value={formData.employmentType} onChange={handleChange}>
                        <option value="">Select</option>
                        <option>Salaried</option>
                        <option>Self-Employed</option>
                        <option>Business</option>
                        <option>Professional</option>
                        </select>
                    </div>

                    <div className="form-field">
                        <label>Company / Business Name</label>
                        <input name="companyName" value={formData.companyName} onChange={handleChange} />
                    </div>

                    <div className="form-field with-icon">
                        <label>Monthly Income (₹)</label>
                        <input
                        name="monthlyIncome"
                        value={formData.monthlyIncome}
                        onChange={handleChange}
                        placeholder="Enter income in ₹"
                        inputMode="numeric"
                        />
                        {/* <i className="fas fa-rupee-sign" /> */}
                        <small className={`hint ${validations.incomeValid ? "ok" : ""}`}>
                        {validations.incomeValid ? "Satisfactory" : "Minimum recommended monthly income is ₹1,000"}
                        </small>
                    </div>

                    <div className="form-field">
                        <label>Work Experience (Years)</label>
                        <input name="workExperience" type="number" value={formData.workExperience} onChange={handleChange} />
                    </div>

                    <div className="form-field with-icon">
                        <label>Office Address</label>
                        <input name="officeAddress" value={formData.officeAddress} onChange={handleChange} />
                        {/* <i className="fas fa-building" /> */}
                    </div>

                    <div className="form-field">
                        <label>Upload Salary Slip / ITR</label>
                        <input name="salarySlip" type="file" accept="application/pdf,image/*" onChange={handleChange} />
                        <small className="hint">Accepted: PDF, JPG, PNG</small>
                    </div>
                    </div>
                </section>
                )}

                {step === 3 && (
                <section className="panel">
                    <h3 className="panel-title">Banking & Loan Details</h3>
                    <div className="form-grid">
                    <div className="form-field">
                        <label>Bank Name</label>
                        <input name="bankName" value={formData.bankName} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Account Number</label>
                        <input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>IFSC Code</label>
                        <input name="ifsc" value={formData.ifsc} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Loan Type</label>
                        <select name="loanType" value={formData.loanType} onChange={handleChange}>
                        <option>Personal Loan</option>
                        <option>Home Loan</option>
                        <option>Business Loan</option>
                        <option>Insta Loan</option>
                        </select>
                    </div>

                    <div className="form-field with-icon">
                        <label>Loan Amount (₹)</label>
                        <input name="loanAmount" value={formData.loanAmount} onChange={handleChange} inputMode="numeric" />
                        {/* <i className="fas fa-rupee-sign" /> */}
                    </div>

                    <div className="form-field">
                        <label>Loan Tenure (Months)</label>
                        <input name="loanTenure" type="number" value={formData.loanTenure} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Purpose of Loan</label>
                        <textarea name="loanPurpose" value={formData.loanPurpose} onChange={handleChange} />
                    </div>

                    {/* Loan info box (fixed interest & EMI) */}
                    <div className="loan-info">
                        <div className="loan-info-row">
                        <div>
                            <div className="loan-info-label">Interest Rate</div>
                            <div className="loan-info-value">{INTEREST_RATE}% p.a. (fixed)</div>
                        </div>
                        <div>
                            <div className="loan-info-label">EMI Estimate</div>
                            <div className="loan-info-value">
                            {formData.loanAmount && formData.loanTenure ? `₹ ${formatINR(monthlyEmiRounded)}` : "—"}
                            <div className="loan-info-sub">Monthly (approx)</div>
                            </div>
                        </div>
                        </div>
                        <div className="loan-info-note">
                        EMI calculated using fixed interest. Final terms subject to verification.
                        </div>
                    </div>
                    </div>
                </section>
                )}

                {step === 4 && (
                <section className="panel">
                    <h3 className="panel-title">References & Documents</h3>
                    <div className="form-grid">
                    <div className="form-field">
                        <label>Reference 1 Name</label>
                        <input name="ref1Name" value={formData.ref1Name} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Reference 1 Mobile</label>
                        <input name="ref1Mobile" value={formData.ref1Mobile} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Reference 2 Name</label>
                        <input name="ref2Name" value={formData.ref2Name} onChange={handleChange} />
                    </div>

                    <div className="form-field">
                        <label>Reference 2 Mobile</label>
                        <input name="ref2Mobile" value={formData.ref2Mobile} onChange={handleChange} />
                    </div>

                    {/* <div className="form-field">
                        <label>Upload Aadhar / ID Proof</label>
                        <input name="idProof" type="file" accept="application/pdf,image/*" onChange={handleChange} />
                        <small className="hint">We'll use this to verify your identity.</small>
                    </div> */}
                    </div>
                </section>
                )}

                {step === 5 && (
                <section className="panel">
                    <h3 className="panel-title">Review & Submit</h3>
                    <div className="review-card">
                    <div className="review-row">
                        <div className="review-col">
                        <strong>Name</strong>
                        <div>{formData.fullName || "N/A"}</div>
                        </div>
                        <div className="review-col">
                        <strong>Mobile</strong>
                        <div>{formData.mobile || "N/A"}</div>
                        </div>
                    </div>

                    <div className="review-row">
                        <div className="review-col">
                        <strong>Email</strong>
                        <div>{formData.email || "N/A"}</div>
                        </div>
                        <div className="review-col">
                        <strong>PAN</strong>
                        <div>{formData.pan || "N/A"}</div>
                        </div>
                    </div>

                    <div className="review-row">
                        <div className="review-col">
                        <strong>Loan</strong>
                        <div>
                            {formData.loanType} • ₹{formatINR(Number(formData.loanAmount || 0))} • {formData.loanTenure || "-"} months
                        </div>
                        </div>

                        <div className="review-col">
                        <strong>EMI</strong>
                        <div>{monthlyEmiRounded ? `₹ ${formatINR(monthlyEmiRounded)}/month` : "N/A"}</div>
                        </div>
                    </div>

                    <div className="review-divider" />

                    <div className="review-block">
                        <strong>Documents</strong>
                        <div>ID Proof: {fileName(formData.idProof)}</div>
                        <div>Salary Slip: {fileName(formData.salarySlip)}</div>
                    </div>
                    </div>
                </section>
                )}

                {/* Navigation */}
                <div className="personal-loan-form-actions">
                <div>
                    {step > 1 && (
                    <button type="button" className="btn secondary" onClick={handleBack}>
                        Back
                    </button>
                    )}
                </div>

                <div className="right-actions">
                    {step < 5 && (
                    <button type="button" className="btn primary" onClick={handleNext}>
                        {step < 4 ? "Continue" : "Review"}
                    </button>
                    )}

                    {step === 5 && (
                    <button type="submit" className="btn primary">
                        Submit Application
                    </button>
                    )}
                </div>
                </div>
            </form>
            </div>

            {/* SUMMARY PANEL */}
            <aside className="summary-panel">
            <h3>Application Summary</h3>

            <div className="summary-section">
                <div className="summary-row">
                <div className="summary-label">Applicant</div>
                <div className="summary-value">{formData.fullName || "—"}</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Mobile</div>
                <div className="summary-value">{formData.mobile || "—"}</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Email</div>
                <div className="summary-value">{formData.email || "—"}</div>
                </div>

                <div className="summary-divider" />

                <div className="summary-row">
                <div className="summary-label">Loan Type</div>
                <div className="summary-value">{formData.loanType}</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Amount</div>
                <div className="summary-value">₹ {formatINR(Number(formData.loanAmount || 0)) || "—"}</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Tenure</div>
                <div className="summary-value">{formData.loanTenure ? `${formData.loanTenure} mo` : "—"}</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Interest (fixed)</div>
                <div className="summary-value">{INTEREST_RATE}% p.a.</div>
                </div>

                <div className="summary-row">
                <div className="summary-label">Estimated EMI</div>
                <div className="summary-value emi">{monthlyEmiRounded ? `₹ ${formatINR(monthlyEmiRounded)}` : "—"}</div>
                </div>

                <div className="summary-divider" />

                <button
                className="btn ghost small"
                onClick={() => {
                    // jump to loan step
                    jumpToStep(3);
                }}
                type="button"
                >
                Edit Loan Details
                </button>
                <button
                className="btn ghost small"
                onClick={() => {
                    jumpToStep(1);
                }}
                type="button"
                >
                Edit Personal Info
                </button>
            </div>
            </aside>
        </div>
        </div>
    );
    };

    export default PersonalLoanLanding;
